import { ArrowRightOutlined } from '@ant-design/icons';
import { EnumCambiamentoStatoRequestTransizione } from '@framework/api-client/api/inquiries-compile/schemas/enumCambiamentoStatoRequestTransizione.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { SubmissionModuleState } from '@framework/common-web/frontoffice/module/fsm/SubmissionModuleState.ts';
import {
  type IModuleStateMachineRecord,
  ModuleStateMachine,
} from '@framework/frontoffice-submission/module/fsm/ModuleStateMachine.ts';
import { ActionButtonContainer } from '@framework/frontoffice-submission/submission/signer/ActionButtonContainer.tsx';
import { ActionMessage } from '@framework/frontoffice-submission/submission/signer/ActionMessage.tsx';
import { SubmissionModuleLogic } from '@framework/frontoffice-submission/submission/submission-module/SubmissionModuleLogic.ts';
import { AsyncButton } from '@framework/ui/components/button/AsyncButton.tsx';
import { Space, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSubmissionModuleStateChanger } from './useSubmissionModuleStateChanger.ts';

export interface IFinishCompilingButtonProps {
  data: IModuleStateMachineRecord;
}

export function FinishCompilingButton(props: IFinishCompilingButtonProps) {
  const { data } = props;

  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const { isDirty } = useFormState();

  const isModuleValid = SubmissionModuleLogic.isValid(
    data.module?.statoValidazione,
  );

  const canBeCompleted = useMemo(() => {
    return ModuleStateMachine.canTransition(data, {
      from: SubmissionModuleState.Compilation,
      to: SubmissionModuleState.Completed,
      user: currentUser,
    });
  }, [currentUser, data]);

  const message = useMemo(() => {
    if (isDirty) {
      return t(
        'submission-action-button.salva-modulo',
        'Salva il modulo per continuare',
      );
    }
    if (!isModuleValid) {
      return t(
        'submission-action-button.modulo-non-valido',
        'Il modulo non è valido',
      );
    }

    return t(
      'submission-action-button.se-completato-il-modulo-premi-compilazione-terminata',
      'Se hai completato il modulo, premi il pulsante Compilazione terminata.',
    );
  }, [isModuleValid, isDirty]);

  const title = useMemo(() => {
    if (isDirty) {
      return t(
        'submission-action-button.salva-modulo',
        'Salva il modulo per continuare',
      );
    }
    if (!isModuleValid) {
      return t(
        'submission-action-button.modulo-non-valido',
        'Il modulo non è valido',
      );
    }

    return undefined;
  }, [isDirty, isModuleValid]);

  const { handleClick } = useSubmissionModuleStateChanger({
    data,
    successMessage: t(
      'submission-action-button.modulo-completato',
      'Modulo completato',
    ),
  });

  if (
    !canBeCompleted.allowed &&
    canBeCompleted.message === 'no-action-permission'
  ) {
    return (
      <ActionMessage
        message={t(
          'submission-action-button.in-attesa-compilazione-delegato',
          'In attesa della compilazione da parte del delegato.',
        )}
        type={'info'}
      />
    );
  }

  if (!canBeCompleted.allowed) return null;

  return (
    <ActionButtonContainer
      message={
        <ActionMessage
          message={message}
          type={isDirty || !isModuleValid ? 'warning' : 'success'}
        />
      }
      button={
        <Tooltip title={title}>
          <AsyncButton
            block
            size="large"
            onClick={() =>
              handleClick(EnumCambiamentoStatoRequestTransizione.COMPLETA)
            }
            disabled={isDirty || !isModuleValid}
          >
            <Space>
              {t(
                'submission-action-button.compilazione-terminata',
                'Compilazione terminata',
              )}
              <ArrowRightOutlined aria-hidden="true" />
            </Space>
          </AsyncButton>
        </Tooltip>
      }
    />
  );
}
